/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {Fragment, useEffect, useRef, useState} from 'react'
import {Combobox, ComboboxButton, ComboboxInput, ComboboxLabel, ComboboxOptions} from '@headlessui/react'
import clsx from 'clsx'
import {ChevronDownIcon, XMarkIcon} from '@heroicons/react/24/solid'
import {useTranslation} from 'react-i18next'
import {ExclamationCircleIcon} from '@heroicons/react/24/outline'
import {Overlay} from 'react-overlays'
import {Company} from 'types/entities/Company'
import SellerAction from 'store/seller/SellerAction'
import {CompaniesAction} from 'store/companies/CompaniesAction'
import {useAppDispatch} from 'helpers/store'
import UiFormLabel from 'components/Ui/UiFormLabel'
import {toast} from 'utils/toastUtils'
import {CompanyBranchType} from 'types/enums/CompanyBranchType'
import CompanyAutocompleteItemsList from './CompanyAutocompleteComponents/CompanyAutocompleteItemsList'
import {getCompanyDisplayName, decodeHtmlEntities} from 'utils/companyUtils'

interface CompanyAutocompleteProps {
    label?: React.ReactNode
    name: string
    placeholder?: string
    className?: string
    classNameInput?: string
    classNameLabel?: string
    required?: boolean
    errors?: any
    disabled?: boolean
    defaultValue?: Company | null
    dropdownDirection?: 'up' | 'down'
    onSelect: (company: Company | null) => void
    onInputChange?: (value: string) => void
    onBlur?: () => void
    allowCreate?: boolean
    onCreate?: (value: string) => void
    autoCreate?: boolean
    showDropdownIcon?: boolean
    createInfoText?: string
}

const CompanyAutocomplete = (props: CompanyAutocompleteProps) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [query, setQuery] = useState('')
    const [selected, setSelected] = useState<Company | null>(props.defaultValue || null)
    const [companies, setCompanies] = useState<Company[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [show, setShow] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const targetRef = useRef(null)
    const selectedOptionRef = useRef<HTMLLIElement>(null)
    const dropdownDirection = props.dropdownDirection || 'down'
    const showDropdownIcon = props.showDropdownIcon !== false

    const ignoreNextQueryChange = useRef(false)

    const canClear = (selected || query) && !props.disabled
    const showOverlay = isFocused && show && (companies.length > 0 || isLoading || Boolean(props.allowCreate && query.trim().length >= 4))
    const showNoResults = !isLoading && companies.length === 0 && query.length >= 2
    const showCreateInfo = Boolean(props.allowCreate && !selected && query.trim().length >= 4 && !isLoading)

    useEffect(() => {
        if (props.defaultValue) {
            ignoreNextQueryChange.current = true
            setSelected(props.defaultValue)
        }
    }, [props.defaultValue])

    useEffect(() => {
        if (!isFocused) { setIsLoading(false); return }

        if (ignoreNextQueryChange.current) {
            ignoreNextQueryChange.current = false
            return
        }
        let timer: ReturnType<typeof setTimeout> | null = null
        if (query.length >= 4) {
            setIsLoading(true)
            setShow(true)
            timer = setTimeout(() => {
                //only look for parents if needed
                dispatch(
                    SellerAction.fetchGetCompanyFromAutocomplete(
                        query,
                        props.name == 'parent_company' ? CompanyBranchType.Parent : null
                    )
                )
                    .then((result: Company[]) => {
                        setCompanies(result)
                        setIsLoading(false)
                    })
                    .catch(() => {
                        setCompanies([])
                        setIsLoading(false)
                    })
            }, 300)
        } else {
            setCompanies([])
            setShow(false)
        }

        return () => {
            if (timer) clearTimeout(timer)
        }
    }, [query, dispatch, isFocused])

    const handleSelect = (company: Company | null) => {
        ignoreNextQueryChange.current = true
        setSelected(company)
        setShow(false)
        props.onSelect(company)
        if (company) {
            const city = company.city ? ` (${company.city})` : ''
            const contactName = [company.first_name, company.last_name].filter(Boolean).join(' ')
            setQuery(`${company.id} • ${decodeHtmlEntities(company.name)}${contactName ? ` • ${contactName}` : ''}${city}`)
        }
    }

    const handleCreate = async (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        if (props.onCreate) {
            props.onCreate(query)
            setShow(false)
            return
        }

        if (props.autoCreate) {
            setIsLoading(true)
            try {
                const newCompany = await dispatch(
                    CompaniesAction.fetchCreateOrUpdateCompany({
                        name: query.trim(),
                        ...(props.name === 'parent_company' ? {type: CompanyBranchType.Parent} : {}),
                    } as Company, {select: false})
                )
                handleSelect(newCompany)
                toast.success(t('Company created successfully'))
            } catch {
                toast.error(t('Failed to create company'))
            } finally {
                setIsLoading(false)
            }
        }
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value
        setQuery(value)
        if (selected) {
            setSelected(null)
            props.onSelect(null)
        }
        if (props.onInputChange) {
            props.onInputChange(value)
        }
        if (!value) {
            setSelected(null)
            props.onSelect(null)
        }
    }

    return (
        <div className={clsx('CompanyAutocomplete w-full relative', props.className)}>
            <div className={clsx('flex flex-col relative w-full')}>
                <Combobox disabled={props.disabled} value={selected} onChange={handleSelect} nullable>
                    {props.label && (
                        <ComboboxLabel as={Fragment}>
                            <UiFormLabel
                                className={clsx('mb-1.5', props.classNameLabel)}
                                isRequired={props.required === true}
                            >
                                {props.label}
                            </UiFormLabel>
                        </ComboboxLabel>
                    )}
                    <div className={clsx('w-full')} onClick={(e) => e.stopPropagation()}>
                        <div className={clsx('relative')}>
                            <ComboboxInput
                                ref={targetRef}
                                id={`input-${props.name}`}
                                data-testid={`input-${props.name}`}
                                className={clsx(
                                    'w-full h-10 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:border-primary-600 focus:ring-0 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-100 placeholder:text-gray-500',
                                    props.disabled &&
                                    'bg-gray-100 text-gray-400 pointer-events-none dark:bg-gray-900 dark:text-gray-500',
                                    props.errors?.[props.name] && 'border-red-500 dark:border-red-500 focus:border-primary-600',
                                    props.classNameInput
                                )}
                                displayValue={(company: Company) => {
                                    if (!company) return query
									const displayName = getCompanyDisplayName(company)
                                    return `${company.id} • ${displayName} `
                                }}
                                onChange={handleInputChange}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => {
                                    setIsFocused(false)
                                    props.onBlur?.()
                                }}
                                placeholder={props.placeholder || t('Search company...')}
                                autoComplete="off"
                            />
							
                            {canClear && (
                                <button
                                    type="button"
                                    className={clsx(
                                        'absolute inset-y-0 flex items-center',
                                        showDropdownIcon ? 'right-7' : 'right-3'
                                    )}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        setQuery('')
                                        setSelected(null)
                                        if (props.onSelect) {
                                            props.onSelect(null as any)
                                        }
                                        if (props.onInputChange) {
                                            props.onInputChange('')
                                        }
                                    }}
                                >
                                    <XMarkIcon
                                        className={clsx('h-4 w-4 text-red-500 hover:text-red-700')}
                                        aria-hidden="true"
                                    />
                                </button>
                            )}
                            {!props.disabled && showDropdownIcon && (
                                <ComboboxButton className={clsx('absolute inset-y-0 right-0 flex items-center pr-3')}>
                                    <ChevronDownIcon className={clsx('h-3.5 w-3.5 text-gray-400')} aria-hidden="true" />
                                </ComboboxButton>
                            )}
                        </div>

                        {(show || companies.length > 0) && (
                            <Overlay
                                placement={dropdownDirection === 'up' ? 'top-start' : 'bottom-start'}
                                show={showOverlay}
                                target={targetRef.current}
                                rootClose
                                onHide={() => setShow(false)}
                            >
                                {({ props: overlayProps, placement }) => (
                                    <ComboboxOptions
                                        {...overlayProps}
                                        static
                                        style={{
                                            ...overlayProps.style,
                                            transition: 'maxHeight 0.3s ease-in-out',
                                            width: targetRef?.current
                                                ? (targetRef.current as HTMLElement).getBoundingClientRect().width
                                                : '100%',
                                        }}
                                        className={clsx(
                                            'transition-all duration-300 ease-in h-auto shadow-2xl z-9999 max-h-60 w-full max-w-full rounded-lg bg-white text-gray-900 overflow-x-hidden overflow-y-auto py-1 text-sm border border-gray-200 dark:border-gray-800',
                                            'dark:bg-gray-900 dark:text-gray-100',
                                            placement
                                        )}
                                    >
                                        {isLoading && (
                                            <div
                                                className={clsx(
                                                    'relative cursor-default select-none py-1.5 px-3 text-xs text-gray-700 dark:text-gray-300'
                                                )}
                                            >
                                                {t('Loading...')}
                                            </div>
                                        )}
                                        {showNoResults && (
                                            <>
                                                <div
                                                    className={clsx(
                                                        'relative cursor-default select-none py-1.5 px-3 text-xs text-gray-700 dark:text-gray-300'
                                                    )}
                                                >
                                                    {t('No companies found')}
                                                </div>
                                            </>
                                        )}
                                        {!isLoading && (
                                            <CompanyAutocompleteItemsList
                                                companies={companies}
                                                selectedOptionRef={selectedOptionRef}
                                            />
                                        )}
                                    </ComboboxOptions>
                                )}
                            </Overlay>
                        )}
                    </div>
                </Combobox>
            </div>

            {props.errors?.[props.name]?.message && (
                <div className={clsx('text-red-500 text-xs mt-1 flex items-center w-full')}>
                    <ExclamationCircleIcon className={clsx('size-4 mr-0.5')} />
                    <span>{props.errors?.[props.name]?.message}</span>
                </div>
            )}
            {showCreateInfo && (
                <div className="mt-1 flex flex-col items-start gap-1 text-xs">
                    <span>{t('Select an existing company or create it before saving.')}</span>
                    <button type="button" className="rounded px-2 py-1 font-medium text-primary-600 focus-visible:outline focus-visible:outline-2 dark:text-primary-400" onClick={handleCreate}>
                        {t('Create new company')}: {query.trim()}
                    </button>
                </div>
            )}
        </div>
    )
}

export default CompanyAutocomplete
